/**
 * Shared primitives for assigning/unassigning cart lines to a gift box.
 * Used by submit-giftbox, save-gift-box-edits, and remove-gift-box.
 */ /**
 * Properties whose values are managed by the gift-box system and should be
 * excluded from the "core" comparison when deciding whether two cart lines
 * represent the same underlying product configuration.
 */ const MERGE_IGNORE_KEYS = new Set([
    '_gift_box_parent_id',
    '_gift_box_id',
    '_gift_box_message'
]);
/**
 * Returns true when two lines have identical core properties — i.e. the same
 * personalisation / embroidery identity — ignoring gift-box management keys.
 *
 * This prevents accidentally merging an embroidered item with a plain variant
 * of the same product just because they share the same variant_id.
 */ export const corePropsMatch = (a, b)=>{
    const keep = (entries)=>entries.filter(([k, v])=>!MERGE_IGNORE_KEYS.has(k) && v != null && v !== '');
    const aEntries = keep(Object.entries(a || {}));
    const bEntries = keep(Object.entries(b || {}));
    if (aEntries.length !== bEntries.length) return false;
    const bMap = new Map(bEntries.map(([k, v])=>[
            k,
            String(v)
        ]));
    return aEntries.every(([k, v])=>bMap.get(k) === String(v));
};
/**
 * Assign `selectedCount` units of `item` to a gift box.
 *
 * - If all units are selected: stamps _gift_box_parent_id on the existing line.
 * - If partial: reduces the original line's quantity by `selectedCount` and
 *   adds a new split line with just those units and _gift_box_parent_id set.
 */ export const assignLineToGiftBox = async (item, selectedCount, parentInstanceId)=>{
    const itemQuantity = Number(item.quantity) || 0;
    if (selectedCount >= itemQuantity) {
        // All units selected — stamp in place.
        // quantity must be included explicitly; omitting it causes Shopify to reset it to 1.
        await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                id: item.key,
                quantity: itemQuantity,
                properties: {
                    ...item.properties || {},
                    _gift_box_parent_id: parentInstanceId
                }
            })
        });
    } else {
        // Partial selection — shrink original line and add new gifted split line
        await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                id: item.key,
                quantity: itemQuantity - selectedCount,
                properties: item.properties || {}
            })
        });
        await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                items: [
                    {
                        id: Number(item.variant_id),
                        quantity: selectedCount,
                        properties: {
                            ...item.properties || {},
                            _gift_box_parent_id: parentInstanceId
                        }
                    }
                ]
            })
        });
    }
};
/**
 * Unassign a gifted child line from its gift box.
 *
 * - If a non-gifted sibling line with the same variant_id exists in `cartItems`:
 *   merges the child's quantity into the sibling and deletes the child line.
 * - Otherwise: clears _gift_box_parent_id in place so it becomes a normal cart item.
 */ export const unassignLineFromGiftBox = async (child, cartItems)=>{
    const siblingLine = cartItems.find((it)=>{
        var _it_properties;
        return it.key !== child.key && it.variant_id === child.variant_id && !((_it_properties = it.properties) === null || _it_properties === void 0 ? void 0 : _it_properties._gift_box_parent_id) && corePropsMatch(it.properties || {}, child.properties || {});
    });
    if (siblingLine) {
        // Delete the gifted split line first so Shopify doesn't see both lines
        // simultaneously when we increase the sibling — that double-count would
        // exceed inventory limits and cause the update to be rejected silently.
        // It is safe to delete the child here because the sibling branch is only
        // reached when a partial split was done; the child is therefore a newly-
        // added line with no embroidery children to cascade-delete.
        await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                id: child.key,
                quantity: 0
            })
        });
        // Now increase the sibling by the child's quantity
        await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                id: siblingLine.key,
                quantity: Number(siblingLine.quantity) + Number(child.quantity)
            })
        });
    } else {
        // No sibling — clear _gift_box_parent_id in place.
        await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                id: child.key,
                quantity: Number(child.quantity),
                properties: {
                    ...child.properties || {},
                    _gift_box_parent_id: null
                }
            })
        });
    }
};
